import {
  type ShallowRef,
  onBeforeUnmount,
  onMounted,
  shallowRef,
  toRefs,
  watch,
} from 'vue'

import { useScroll } from '@vueuse/core'

/**
 * Tracking of the carousel:
 * The tracking only happens when the user scrolls to the right
 * and the item is at least 50% visible.
 *
 * We only need to know the carousel has been interacted with
 * and the current slide.
 */
export const useCarouselImpression = (
  listRef: Readonly<ShallowRef<HTMLUListElement | null>>,
  itemsRef: Readonly<ShallowRef<HTMLLIElement[] | null>>,
) => {
  const { isScrolling, directions } = useScroll(listRef)
  const { right: toRight } = toRefs(directions)

  const lastIntersectedItem = shallowRef<HTMLLIElement | null>(null)

  const onImpression = (callback: (data: { currentSlide: number }) => void) => {
    watch(
      [isScrolling, toRight, lastIntersectedItem],
      ([isCurrentlyScrolling, isMovingRight, currentIntersectedItem]) => {
        if (isCurrentlyScrolling && isMovingRight && currentIntersectedItem) {
          const currentSlide =
            itemsRef.value?.indexOf(currentIntersectedItem) ?? 0

          callback({ currentSlide })
        }
      },
    )
  }

  let observer: IntersectionObserver | null = null
  onMounted(() => {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            lastIntersectedItem.value !== entry.target
          ) {
            lastIntersectedItem.value = entry.target as HTMLLIElement
          }
        })
      },
      { threshold: 0.5 },
    )

    itemsRef.value?.forEach((item) => {
      observer?.observe(item)
    })
  })

  onBeforeUnmount(() => {
    if (observer) {
      itemsRef.value?.forEach((item) => {
        observer?.unobserve(item)
      })
      observer.disconnect()
    }
  })

  return {
    onImpression,
  }
}
